import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazy-retry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Sentry } from "@/lib/sentry";
import ScrollToTop from "./components/ScrollToTop.tsx";
import AttributionTracker from "./components/AttributionTracker.tsx";

// Eagerly load the homepage — it's the LCP-critical route.
import Index from "./pages/Index.tsx";

// Routes wrapped so Sentry records navigation transactions per route.
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Everything else is code-split so it doesn't inflate the initial bundle.
const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail.tsx"));
const PropertyQuote = lazy(() => import("./pages/PropertyQuote.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const ChannelListingRedirect = lazy(() => import("./pages/ChannelListingRedirect.tsx"));
const ChannelClaim = lazy(() => import("./pages/ChannelClaim.tsx"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation.tsx"));
const MyBookings = lazy(() => import("./pages/MyBookings.tsx"));
const GuestAuth = lazy(() => import("./pages/GuestAuth.tsx"));
const Wishlist = lazy(() => import("./pages/Wishlist.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const Onboard = lazy(() => import("./pages/Onboard.tsx"));
const ApplicationStatus = lazy(() => import("./pages/ApplicationStatus.tsx"));
const CancellationPolicy = lazy(() => import("./pages/CancellationPolicy.tsx"));
const TravelProtection = lazy(() => import("./pages/TravelProtection.tsx"));
const TermsOfService = lazy(() => import("./pages/TermsOfService.tsx"));
const PropertyManagerAgreement = lazy(() => import("./pages/PropertyManagerAgreement.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const QuickBook = lazy(() => import("./pages/admin/QuickBook.tsx"));
const TaxReports = lazy(() => import("./pages/admin/TaxReports.tsx"));
const LinkBuilder = lazy(() => import("./pages/admin/LinkBuilder.tsx"));
const ConciergeReview = lazy(() => import("./pages/admin/ConciergeReview.tsx"));
const SystemHealth = lazy(() => import("./pages/admin/SystemHealth.tsx"));
const InsuranceStatus = lazy(() => import("./pages/admin/InsuranceStatus.tsx"));
const AdminOnboardingInsights = lazy(() => import("./pages/admin/AdminOnboardingInsights.tsx"));
const AdminRewards = lazy(() => import("./pages/admin/Rewards.tsx"));
const AdminPromoCodes = lazy(() => import("./pages/admin/PromoCodes.tsx"));
const AdminOutreach = lazy(() => import("./pages/admin/Outreach.tsx"));
const SocialCalendar = lazy(() => import("./pages/admin/SocialCalendar.tsx"));
const SeoTrends = lazy(() => import("./pages/admin/SeoTrends.tsx"));
const AnalyticsAgent = lazy(() => import("./pages/admin/AnalyticsAgent.tsx"));
const CheckoutPay = lazy(() => import("./pages/CheckoutPay.tsx"));

const OAuthConsent = lazy(() => import("./pages/OAuthConsent.tsx"));
const ConnectAI = lazy(() => import("./pages/ConnectAI.tsx"));
const AskAI = lazy(() => import("./pages/AskAI.tsx"));
const LocationLanding = lazy(() => import("./pages/LocationLanding.tsx"));
const GuidePage = lazy(() => import("./pages/GuidePage.tsx"));
const BookingFeesExplained = lazy(() => import("./pages/BookingFeesExplained.tsx"));
const EsPanamaCityBeach = lazy(() => import("./pages/EsPanamaCityBeach.tsx"));
const OwnerPortal = lazy(() => import("./pages/OwnerPortal.tsx"));
const Partners = lazy(() => import("./pages/Partners.tsx"));
const Inquire = lazy(() => import("./pages/Inquire.tsx"));
const Rewards = lazy(() => import("./pages/Rewards.tsx"));
const ManagerDashboard = lazy(() => import("./pages/ManagerDashboard.tsx"));
const ManagerPmsSetup = lazy(() => import("./pages/ManagerPmsSetup.tsx"));
const ManagerPropertyEditor = lazy(() => import("./pages/ManagerPropertyEditor.tsx"));
const PmsOAuthCallback = lazy(() => import("./pages/PmsOAuthCallback.tsx"));
const EarningsCalculator = lazy(() => import("./pages/EarningsCalculator.tsx"));


const PartnerProfile = lazy(() => import("./pages/PartnerProfile.tsx"));
const VrboVsAirbnbForHosts = lazy(() => import("./pages/VrboVsAirbnbForHosts.tsx"));
const PcbPropertyManagement = lazy(() => import("./pages/PcbPropertyManagement.tsx"));
const ListYourAirbnb = lazy(() => import("./pages/ListYourAirbnb.tsx"));
const ConciergePortal = lazy(() => import("./pages/ConciergePortal.tsx"));
const ConciergeQRCodes = lazy(() => import("./pages/admin/ConciergeQRCodes.tsx"));
const ConciergeFinder = lazy(() => import("./pages/ConciergeFinder.tsx"));
const ConsumerConcierge = lazy(() => import("./pages/admin/ConsumerConcierge.tsx"));
const DirectBookingPlaybook = lazy(() => import("./pages/admin/DirectBookingPlaybook.tsx"));
const OwnerRezOnboardingGuide = lazy(() => import("./pages/admin/OwnerRezOnboardingGuide.tsx"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-accent" />
  </div>
);

/**
 * Concierge chat temporarily hidden (Sep 2026) — unused by guests and clunky
 * on mobile. Code kept intact: restore by rendering <ConciergeChat /> here,
 * or flip the `concierge_enabled` app setting via Admin > Settings.
 */
const ConciergeMount = () => {
  return null;
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AttributionTracker />
          <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <SentryRoutes>
                <Route path="/" element={<Index />} />
                <Route path="/property/:slug/quote" element={<PropertyQuote />} />
                <Route path="/property/:slug" element={<PropertyDetail />} />
                <Route path="/orez/listing/:orezListingId" element={<ChannelListingRedirect />} />
                <Route path="/orez/claim/:token" element={<ChannelClaim />} />

                <Route path="/booking/:token" element={<BookingConfirmation />} />
                <Route path="/my-bookings" element={<MyBookings />} />
                <Route path="/my-bookings/:bookingId" element={<BookingConfirmation />} />
                <Route path="/guest/auth" element={<GuestAuth />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/onboard" element={<Onboard />} />
                <Route path="/application-status" element={<ApplicationStatus />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                {/* Deep links to a dashboard section, e.g. /admin/bookings. Static admin routes above win by specificity. */}
                <Route path="/admin/:section" element={<AdminDashboard />} />

                <Route path="/admin/quick-book" element={<QuickBook />} />
                <Route path="/admin/tax-reports" element={<TaxReports />} />
                <Route path="/admin/link-builder" element={<LinkBuilder />} />
                <Route path="/admin/concierge-review" element={<ConciergeReview />} />
                <Route path="/admin/system-health" element={<SystemHealth />} />
                <Route path="/admin/insurance-status" element={<InsuranceStatus />} />
                <Route path="/admin/onboarding-insights" element={<AdminOnboardingInsights />} />
                <Route path="/admin/rewards" element={<AdminRewards />} />
                <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
                <Route path="/admin/outreach" element={<AdminOutreach />} />
<Route path="/admin/social-calendar" element={<SocialCalendar />} />
                <Route path="/admin/seo-trends" element={<SeoTrends />} />
                <Route path="/admin/analytics-agent" element={<AnalyticsAgent />} />
                <Route path="/checkout/pay" element={<CheckoutPay />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/travel-protection" element={<TravelProtection />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/property-manager-agreement" element={<PropertyManagerAgreement />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/connect-ai" element={<ConnectAI />} />
                <Route path="/ask-ai-about-panama-city-beach-rentals" element={<AskAI />} />
                <Route path="/ask-ai" element={<Navigate to="/ask-ai-about-panama-city-beach-rentals" replace />} />

                <Route path="/owner" element={<OwnerPortal />} />
                <Route path="/manager" element={<ManagerDashboard />} />
                <Route path="/manager/pms-setup" element={<ManagerPmsSetup />} />
                <Route path="/manager/properties/:id" element={<ManagerPropertyEditor />} />
                <Route path="/manager/pms-callback/:provider" element={<PmsOAuthCallback />} />
                <Route path="/earnings-calculator" element={<EarningsCalculator />} />
                

                <Route path="/partner/:slug" element={<PartnerProfile />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/inquire" element={<Inquire />} />
                <Route path="/rewards" element={<Rewards />} />
                <Route path="/vrbo-vs-airbnb-for-hosts" element={<VrboVsAirbnbForHosts />} />

                <Route path="/panama-city-beach-property-management" element={<PcbPropertyManagement />} />
                <Route path="/list-your-airbnb" element={<ListYourAirbnb />} />

                {/* In-unit digital concierge (QR / iPad) */}
                <Route path="/concierge/:slug" element={<ConciergePortal />} />
                <Route path="/admin/concierge-qr" element={<ConciergeQRCodes />} />

                {/* Amplify Concierge — direct booking finder */}
                <Route path="/concierge" element={<ConciergeFinder />} />
                <Route path="/find-direct-rates-worldwide" element={<Navigate to="/concierge" replace />} />
                <Route path="/admin/consumerconcierge" element={<ConsumerConcierge />} />



                {/* SEO location landing pages */}
                <Route path="/panama-city-beach-vacation-rentals" element={<LocationLanding slug="panama-city-beach-vacation-rentals" />} />
                <Route path="/pcb-beachfront-rentals" element={<LocationLanding slug="pcb-beachfront-rentals" />} />
                <Route path="/pcb-pet-friendly-rentals" element={<LocationLanding slug="pcb-pet-friendly-rentals" />} />
                <Route path="/pcb-rentals-with-pool" element={<LocationLanding slug="pcb-rentals-with-pool" />} />
                <Route path="/pcb-private-pool-beach-houses" element={<LocationLanding slug="pcb-private-pool-beach-houses" />} />

                <Route path="/pcb-large-group-rentals" element={<LocationLanding slug="pcb-large-group-rentals" />} />
                {/* Retired monthly/snowbird landing page → main PCB rentals hub (301 also in public/_redirects) */}
                <Route path="/pcb-monthly-winter-rentals" element={<Navigate to="/panama-city-beach-vacation-rentals" replace />} />
                <Route path="/pcb-condo-rentals" element={<LocationLanding slug="pcb-condo-rentals" />} />
                <Route path="/pcb-family-friendly-rentals" element={<LocationLanding slug="pcb-family-friendly-rentals" />} />
                

                {/* Top-of-funnel utility guides */}
                <Route path="/getting-to-panama-city-beach" element={<GuidePage slug="getting-to-panama-city-beach" />} />
                <Route path="/panama-city-beach-access-map" element={<GuidePage slug="panama-city-beach-access-map" />} />
                <Route path="/best-time-to-visit-panama-city-beach" element={<GuidePage slug="best-time-to-visit-panama-city-beach" />} />
                <Route path="/panama-city-beach-spring-break-dates" element={<GuidePage slug="panama-city-beach-spring-break-dates" />} />
                <Route path="/things-to-do-in-panama-city-beach" element={<GuidePage slug="things-to-do-in-panama-city-beach" />} />
                <Route path="/panama-city-beach-events-calendar" element={<GuidePage slug="panama-city-beach-events-calendar" />} />
                <Route path="/booking-fees-explained" element={<BookingFeesExplained />} />
                <Route path="/airbnb-fees-explained" element={<GuidePage slug="airbnb-fees-explained" />} />
                <Route path="/vrbo-fees-explained" element={<GuidePage slug="vrbo-fees-explained" />} />
                <Route path="/expedia-fees-explained" element={<GuidePage slug="expedia-fees-explained" />} />
                {/* Retired URLs — 301s live in public/_redirects; these are SPA fallbacks */}
                <Route path="/booking-com-fees-explained" element={<GuidePage slug="booking-com-fees-explained" />} />
                <Route path="/what-is-my-rental-worth" element={<Navigate to="/earnings-calculator" replace />} />
                {/* Internal sales playbook — admin only, retired from public SEO */}
                <Route path="/admin/direct-booking-playbook" element={<DirectBookingPlaybook />} />
                <Route path="/how-to-find-a-direct-booking-site" element={<Navigate to="/concierge" replace />} />
                {/* Internal OwnerRez onboarding guide — admin only until partner approval */}
                <Route path="/admin/ownerrez-onboarding-guide" element={<OwnerRezOnboardingGuide />} />


                {/* Spanish landing page — demand test for full i18n */}
                <Route path="/es/alquileres-vacacionales-panama-city-beach" element={<EsPanamaCityBeach />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </SentryRoutes>
            </Suspense>
          </ErrorBoundary>
          <ConciergeMount />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
